import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Button, Modal } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import api from 'api';
import InputField from 'components/InputField';
import validations from 'validations';
import { InvitedBroker } from '../../models/InvitedBroker';
import PhoneFieldWrapper from 'components/PhoneFieldWrapper';
import { sentryError } from 'utils';
import { getInitialPhone } from 'phoneNumberUtils';
import DropzoneField from 'components/DropzoneField';
import { DropzoneUpload } from 'models/Dropzone';
import { DropzoneFile } from 'components/Dropzone/utils';
import EmailField from 'components/EmailField';

import './AddABrokerModal.styles.scss';

type AddABrokerModalProps = {
  className?: string;
  addInvitedBroker: (invited: InvitedBroker) => void;
};

const AddABrokerModal: React.ForwardRefRenderFunction<
  OverlayRef,
  AddABrokerModalProps
> = (props, ref) => {
  const { className, addInvitedBroker } = props;
  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  const { t } = useTranslation();
  const { popSuccess, popServerError } = useContext(PopNotificationsContext);
  const [firmLogo, setFirmLogo] = useState<DropzoneFile>(null);

  const classes = classNames('add-a-broker-modal', className);
  useImperativeHandle(ref, () => modal, [modal]);

  const uploadBrokerFirmLogo: DropzoneUpload = useCallback(
    async (file, fileName, config) => {
      try {
        const data = await api.portfolio.admin.uploadBrokerFirmLogo(
          file,
          fileName,
          config,
        );

        setFirmLogo(data);
        return data;
      } catch (error) {
        sentryError(error);
      }
    },
    [],
  );

  const deleteBrokerFirmLogo = useCallback(async () => {
    try {
      await api.portfolio.admin.deleteBrokerFirmLogo(firmLogo.key);
      setFirmLogo(null);
      return null;
    } catch (error) {
      sentryError(error);
    }
  }, [firmLogo]);

  const handleOnSubmit = useCallback(
    async (values) => {
      try {
        const { data } = await api.portfolio.admin.addABroker({
          ...values,
          firmLogo,
        });

        addInvitedBroker(data);
        popSuccess(t('successfullyCreatedBroker'));
        modal.close();
      } catch (e) {
        sentryError(e);
        popServerError(e);
      }
    },
    [addInvitedBroker, firmLogo, modal, popServerError, popSuccess, t],
  );

  return (
    <Modal ref={modalRef} className={classes} title={t('addABroker')}>
      <Form
        keepDirtyOnReinitialize
        initialValues={{
          phone: getInitialPhone(),
        }}
        onSubmit={handleOnSubmit}
        render={({ handleSubmit }) => {
          return (
            <form onSubmit={handleSubmit} className="add-a-broker-modal__form">
              <div className="add-a-broker-modal__form__row">
                <Field
                  name="firstName"
                  label={t('firstName')}
                  placeholder={t('firstName')}
                  component={InputField}
                  validate={validations.composeValidators(
                    validations.required(),
                    validations.noLongerThan(
                      50,
                      t('noLongerThan', { length: 50 }),
                    ),
                    validations.fullNameValidation(
                      t('specialCharactersOrNumbersNotAllowed'),
                    ),
                  )}
                  required
                />
                <Field
                  name="lastName"
                  label={t('lastName')}
                  placeholder={t('lastName')}
                  component={InputField}
                  required
                  validate={validations.composeValidators(
                    validations.required(),
                    validations.noLongerThan(
                      50,
                      t('noLongerThan', { length: 50 }),
                    ),
                    validations.fullNameValidation(
                      t('specialCharactersOrNumbersNotAllowed'),
                    ),
                  )}
                />
              </div>

              <div className="add-a-broker-modal__form__row">
                <EmailField required />
                <Field
                  name="linkedinAccount"
                  label={t('linkedIn')}
                  placeholder={t('personalLinkedinUrl')}
                  component={InputField}
                  validate={validations.linkedinValidation()}
                />
              </div>

              <PhoneFieldWrapper />

              <Field
                className="company-logo-dropzone"
                accept="image/*"
                maxFiles={1}
                label={t('firmLogo')}
                name="firmLogo"
                component={DropzoneField}
                uploadRequest={uploadBrokerFirmLogo}
                deleteRequest={deleteBrokerFirmLogo}
              />
              <Button type="submit">{t('inviteThisBroker')}</Button>
            </form>
          );
        }}
      />
    </Modal>
  );
};

export default forwardRef(AddABrokerModal);
